import { writable, get } from "$utils/shim";
import KnockClient from "@knocklabs/client";

export function createNotificationsStore() {
  const store = writable({
    notifications: [],
    unreadCount: 0,
    totalCount: 0,
    isWaiting: true,
    feedClient: null,
    isInitialized: false,
  });

  const { subscribe, set, update } = store;

  function upsertNotifications({ items, metadata }) {
    update((state) => {
      // Merge the received notifications with existing ones
      // New notifications should be prepended, existing ones updated
      const updatedNotifications = [...state.notifications];

      items.forEach((newItem) => {
        const existingIndex = updatedNotifications.findIndex((item) => item.id === newItem.id);

        if (existingIndex >= 0) {
          // Update existing notification
          updatedNotifications[existingIndex] = newItem;
        } else {
          // Prepend new notification
          updatedNotifications.unshift(newItem);
        }
      });

      updatedNotifications.sort((a, b) => {
        return new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime();
      });

      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: metadata.unread_count,
        totalCount: metadata.total_count,
        isWaiting: false,
      };
    });
  }

  return {
    subscribe: store.subscribe,

    init: async ({ public_key, signed_key, org_user_id, channel_id }) => {
      return update((state) => {
        if (state.isInitialized) {
          return state;
        }

        const knockClient = new KnockClient(public_key);

        knockClient.authenticate(org_user_id, signed_key);

        const feedClient = knockClient.feeds.initialize(channel_id);

        // Setup listeners
        feedClient.on("items.received.page", ({ items, metadata }) => {
          upsertNotifications({ items, metadata });
        });

        // Real-time notifications
        feedClient.on("items.received.realtime", (params) => {
          upsertNotifications(params);
        });

        // Start listening for updates
        feedClient.listenForUpdates();

        // Initial fetch
        feedClient.fetch();

        return {
          ...state,
          feedClient,
          isInitialized: true,
        };
      });
    },

    toggleNotifications: () => {
      update((state) => ({ ...state, isOpen: !state.isOpen }));
    },

    markAllRead: async () => {
      update((state) => {
        if (state.feedClient) {
          state.feedClient.markAllAsRead();

          // Update all notifications to be read
          const updatedNotifications = state.notifications.map((notif) => ({
            ...notif,
            read_at: notif.read_at || new Date().toISOString(),
          }));

          return {
            ...state,
            notifications: updatedNotifications,
            unreadCount: 0,
          };
        }
        return state;
      });
    },

    markRead: async (id) => {
      const state = get(store);
      if (state.feedClient) {
        // Optimistic read
        state.feedClient.markAsRead({ id });

        update((state) => {
          const updatedNotifications = [...state.notifications];
          const item = updatedNotifications.find((item) => item.id === id);

          if (item && !item.read_at) {
            item.read_at = new Date().toISOString();
            return {
              ...state,
              notifications: updatedNotifications,
              unreadCount: Math.max(0, state.unreadCount - 1),
            };
          }

          return state;
        });
      }
    },

    archive: async (id) => {
      const state = get(store);
      if (state.feedClient) {
        // Optimistic delete
        state.feedClient.markAsArchived({ id });

        update((state) => {
          // Remove the archived notification from the list
          const updatedNotifications = state.notifications.filter((item) => item.id !== id);

          // If it was unread, decrement the count
          const wasUnread = state.notifications.find((item) => item.id === id && !item.read_at);

          return {
            ...state,
            notifications: updatedNotifications,
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
            totalCount: Math.max(0, state.totalCount - 1),
          };
        });
      }
    },
  };
}
